import { User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

/**
 * The identity a candidate is being compared against, as far as the caller's
 * detail already knows it — no extra request is made for the comparison.
 */
export interface MatchReferenceIdentity {
  /** Already translated, so the card never has to guess a fallback. */
  displayName: string
  /** A URL the caller already resolved; the bytes sit behind the files endpoint. */
  photoUrl: string | null
  /** The graph id of the person, shown verbatim for cross-referencing. */
  personId?: string | null
  /** Confirmed faces used as references for this identity. */
  referenceCount?: number | null
  /** Pending suggestions left on this identity, unfiltered. */
  pendingTotal?: number | null
}

/**
 * The reference side of a comparison: the identity's main photo, its name and
 * the identifying figures the detail query already carries. Pure — the photo
 * arrives as a resolved URL — so the overlay's focused face row and anything else
 * that has to name the other half of a match can mount it.
 */
export function MatchReferenceCard({
  identity,
  compact = false,
  className,
}: {
  identity: MatchReferenceIdentity
  /**
   * Keep the photo beside the name at every width. The overlay's rail is a narrow
   * column inside a wide viewport, so the stacked variant would blow the photo up
   * to the width of the rail there.
   */
  compact?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const base = 'cases.investigate.entityResolution.photoMatching'
  const { displayName, photoUrl, personId, referenceCount, pendingTotal } = identity

  const facts = [
    referenceCount != null ? { key: 'referenceFaces', value: String(referenceCount) } : null,
    pendingTotal != null ? { key: 'pending', value: String(pendingTotal) } : null,
  ].filter(fact => fact !== null)

  return (
    <section
      aria-label={t(`${base}.comparison.reference.title`)}
      data-testid="match-reference-card"
      className={cn(
        'flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-3',
        className
      )}
    >
      <h3 className="text-2xs uppercase tracking-wider text-muted-foreground">
        {t(`${base}.comparison.reference.title`)}
      </h3>

      <div
        className={cn('flex min-w-0 items-center gap-3', !compact && 'lg:flex-col lg:items-start')}
      >
        <div
          className={cn(
            'flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted',
            !compact && 'lg:aspect-square lg:size-auto lg:w-full'
          )}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={t(`${base}.comparison.reference.photoAlt`, { name: displayName })}
              className="size-full object-cover"
            />
          ) : (
            <User className="size-8 text-muted-foreground" aria-hidden />
          )}
        </div>
        <div className={cn('flex min-w-0 flex-col gap-1', !compact && 'lg:w-full')}>
          <p className="truncate text-base font-medium" title={displayName}>
            {displayName}
          </p>
          {photoUrl ? null : (
            <p className="text-xs text-muted-foreground">
              {t(`${base}.comparison.reference.noPhoto`)}
            </p>
          )}
        </div>
      </div>

      <dl className="flex flex-col gap-3 text-sm">
        {facts.map(fact => (
          <div key={fact.key} className="flex flex-col gap-1">
            <dt className="text-2xs uppercase tracking-wider text-muted-foreground">
              {t(`${base}.comparison.reference.${fact.key}`)}
            </dt>
            <dd className="tabular-nums">{fact.value}</dd>
          </div>
        ))}
        {personId ? (
          <div className="flex min-w-0 flex-col gap-1">
            <dt className="text-2xs uppercase tracking-wider text-muted-foreground">
              {t(`${base}.comparison.reference.identityId`)}
            </dt>
            <dd className="truncate font-mono text-xs text-muted-foreground" title={personId}>
              {personId}
            </dd>
          </div>
        ) : null}
      </dl>
    </section>
  )
}
